import React, { FC, ReactNode, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ConfigContext, FetalSexContext } from "@app/provider";
import { CaPnsSupplementalDialog } from "@app/components";
import { FetalSexDialog } from "@app/components/viewResultDialogs";
import { useDialog } from "@natera/platform/lib/hooks";
import {
  ParsedTestCard,
  ProsperaOrganType,
  TestCardStatus,
  TestType,
} from "@app/provider/testData/types";
import { routes } from "@app/routing";
import { GenderOption } from "./testResult";

interface ViewResultController {
  openResultPage: (testCard: ParsedTestCard, isHistory?: boolean) => void;
  openTestResultDetailsPage: (
    testCard: ParsedTestCard,
    isHistory?: boolean
  ) => void;
  openShareFetalSexPage: (
    testCard: ParsedTestCard,
    isHistory?: boolean
  ) => void;
}

interface ViewResultsProvider {
  children: ReactNode;
}

export const Context = React.createContext<ViewResultController>({
  openResultPage: () => undefined,
  openTestResultDetailsPage: () => undefined,
  openShareFetalSexPage: () => undefined,
});

export const isNeedPreTestInfo = (testType: TestType): boolean =>
  [
    TestType.VISTARA,
    TestType.EMPOWER,
    TestType.PROSPERA,
    TestType.RENASIGHT,
    TestType.ALTERA,
    TestType.SIGNATERA,
    TestType.SINGLE_GENE_NIPT,
    TestType.LATITUDE,
    TestType.DPYD,
  ].includes(testType);

Context.displayName = "ViewResultContext";

const ViewResultProvider: FC<ViewResultsProvider> = ({ children }) => {
  const fetalSexDialog = useDialog(FetalSexDialog);
  const caPnsSupplementalDialog = useDialog(CaPnsSupplementalDialog);
  const navigate = useNavigate();
  const location = useLocation();
  const { getFetalSexIsShown } = useContext(FetalSexContext);
  const { config } = useContext(ConfigContext);

  /** Preserve back target (e.g. test history) on PDF / pre-test navigations. */
  const resolveResultNavState = (isHistory?: boolean) =>
    isHistory ? { from: routes.testHistory } : location.state;

  const openTestResultDetailsPage = (
    testCard: ParsedTestCard,
    isHistory?: boolean
  ) => {
    let from;
    if (isHistory) {
      from = routes.testHistory;
    }
    openResultDetailsPage(testCard.orderUidPublic, testCard.uid, from);
  };

  const openResultDetailsPage = (
    orderUidPublic: string,
    testUid: string,
    from?: string
  ) =>
    navigate(routes.testResultDetailsPage(orderUidPublic, testUid), {
      state: from !== undefined ? { from } : undefined,
    });

  const openPdfPage = (
    orderUidPublic: string,
    testUid: string,
    testDocumentUid: string,
    isHistory?: boolean
  ) =>
    navigate(
      routes.testResultPdfPage(orderUidPublic, testUid, testDocumentUid),
      {
        state: resolveResultNavState(isHistory),
      }
    );

  const openPreTestInfoPage = (
    orderUidPublic: string,
    testUid: string,
    status: TestCardStatus,
    organType: ProsperaOrganType | undefined,
    testDocumentUid: string,
    isHistory?: boolean
  ) =>
    navigate(
      `${routes.understandingYourResultsPage(
        orderUidPublic,
        testUid,
        status,
        testDocumentUid
      )}${organType ? `?organType=${organType}` : ""}`,
      { state: resolveResultNavState(isHistory) }
    );

  const openResultPage = (testCard: ParsedTestCard, isHistory?: boolean) => {
    const {
      testType,
      orderUidPublic,
      uid,
      isNevaEnabled,
      latestResultDocumentUid,
    } = testCard;

    if (testType === TestType.PANORAMA) {
      handlePanoramaResult(testCard, isHistory);
    } else if (
      isNeedPreTestInfo(testCard.testType) &&
      !(isNevaEnabled && config.nevaConfig.enabled)
    ) {
      handleTestWithPreResultInfo(testCard, isHistory);
    } else {
      openPdfPage(
        orderUidPublic,
        uid,
        latestResultDocumentUid ?? "",
        isHistory
      );
    }
  };

  const openShareFetalSexPage = (testCard: ParsedTestCard) => {
    navigate(routes.shareFetalSexPage(testCard.orderUidPublic, testCard.uid));
  };

  const handlePanoramaResult = async (
    testCard: ParsedTestCard,
    isHistory?: boolean
  ) => {
    const fetalSexFlag = await getFetalSexIsShown(testCard.orderUidPublic);
    const fetalSexIsShown = fetalSexFlag?.fetalSexIsShown;

    const invalidFetalSexValues = [
      GenderOption.UNKNOWN,
      GenderOption.MASKED,
      GenderOption.NOT_REPORTED,
    ];

    const isRequiredToAskAboutFetalSex =
      testCard.reportGender &&
      testCard.resultCode !== "TNP" &&
      (!testCard.fetalSex ||
        !invalidFetalSexValues.includes(testCard.fetalSex)) &&
      !fetalSexIsShown;

    if (testCard.caPnsSupplemental) {
      caPnsSupplementalDialog.open({
        onCloseDialog: () => caPnsSupplementalDialog.close(),
        orderUidPublic: testCard.orderUidPublic,
        testUid: testCard.uid,
        testDocumentUid: testCard.latestResultDocumentUid ?? "",
      });
    } else if (isRequiredToAskAboutFetalSex) {
      fetalSexDialog.open({
        onCloseDialog: () => fetalSexDialog.close(),
        orderUidPublic: testCard.orderUidPublic,
        testUid: testCard.uid,
        testDocumentUid: testCard.latestResultDocumentUid ?? "",
        limsClinicId: testCard.clinic?.limsId,
      });
    } else {
      openPdfPage(
        testCard.orderUidPublic,
        testCard.uid,
        testCard.latestResultDocumentUid ?? "",
        isHistory
      );
    }
  };

  const handleTestWithPreResultInfo = async (
    testCard: ParsedTestCard,
    isHistory?: boolean
  ) => {
    const isEmpowerStatAlreadyViewed =
      testCard.status === TestCardStatus.RESULT_READY &&
      testCard.testType === TestType.EMPOWER &&
      testCard.isLatestReleasedResultStat &&
      testCard.isStatResultAlreadyViewed;

    if (
      testCard.status == TestCardStatus.RESULT_READY &&
      !isEmpowerStatAlreadyViewed
    ) {
      openPreTestInfoPage(
        testCard.orderUidPublic,
        testCard.uid,
        testCard.status,
        testCard.organType,
        testCard.latestResultDocumentUid ?? "",
        isHistory
      );
    } else {
      openPdfPage(
        testCard.orderUidPublic,
        testCard.uid,
        testCard.latestResultDocumentUid ?? "",
        isHistory
      );
    }
  };

  const viewResultController: ViewResultController = React.useMemo(
    () => ({
      openTestResultDetailsPage,
      openResultPage,
      openShareFetalSexPage,
    }),
    [openTestResultDetailsPage, openResultPage, openShareFetalSexPage]
  );

  return (
    <Context.Provider value={viewResultController}>
      {fetalSexDialog.getDialog()}
      {caPnsSupplementalDialog.getDialog()}
      {children}
    </Context.Provider>
  );
};

export default ViewResultProvider;
